import { Route, Routes, useMatch, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useRollbar, useRollbarPerson } from "@rollbar/react";
import { useEffect } from "react";

import Layout from "./components/layout/Layout";

import Home from "./pages/Home";
import HomeAnom from "./pages/HomeAnom";

import RecentList from "./components/lists/listComponents/RecentList";
import WaitingList from "./components/lists/listComponents/WaitingList";
import ReadyList from "./components/lists/listComponents/ReadyList";
import ActiveList from "./components/lists/listComponents/ActiveList";

import CreateCampaignRoot from "./pages/CreateCampaign/CreateCampaignRoot";
import CreateCampaign from "./pages/CreateCampaign/CreateCampaign";
import SelectWebsites from "./pages/CreateCampaign/SelectWebsites";
import CreateMetaArticle from "./pages/CreateCampaign/CreateMetaArticle";
import MarketplaceGuard from "./components/forms/Campaign/MarketplaceGuard";


import CampaignsTest from "./pages/CampaignsTest";

import CampaignRoot from "./pages/Campaign/CampaignRoot";
import Campaign from "./pages/Campaign/Campaign";
import MetaArticle from "./pages/Campaign/MetaArticle";
import TrafficDrivers from "./pages/Campaign/TrafficDrivers";
import Report from "./pages/Campaign/Report";
import ReportNew from "./pages/Campaign/ReportNew";
import ReportNewTime from "./pages/Campaign/ReportNewTime";
import ReportNewTimePrototype from "./pages/Campaign/ReportNewTimePrototype";
import ReportVideo from "./pages/Campaign/ReportVideo";
import Details from "./pages/Campaign/Details";
import Websites from "./pages/Campaign/Websites";

import Publication from "./pages/Campaign/Publication/Publication";
import PublicationDetails from "./pages/Campaign/Publication/Details";
import PublicationContent from "./pages/Campaign/Publication/Content";

import EmailTemplate from "./pages/email/EmailTemplate";

import {
  useFetchCurrentUser,
  CurrentUserContext,
} from "./utils/CurrentUserContext";
import SettingsAdvanced from "./pages/Settings/SettingsAdvanced";
import Spinner from "./components/icons/Spinner";
import { identifyUser, trackEvent, resetAnalytics } from "./utils/analytics";
import AdvancedProfile from "./pages/Settings/SettingsComponents/AdvancedProfile";
import Notifications from "./pages/Settings/SettingsComponents/Notifications";
import AdvancedBilling from "./pages/Settings/SettingsComponents/AdvancedBilling";
import Revenue from "./pages/Settings/SettingsComponents/Revenue";
import AdvancedAccount from "./pages/Settings/SettingsComponents/AdvancedAccount";
import CreateUserForm from "./pages/Settings/SettingsComponents/CreateUserForm";
import AdvancedWebsites from "./pages/Settings/SettingsComponents/AdvancedWebsites";
import AdvancedPlacements from "./pages/Settings/SettingsComponents/AdvancedPlacements";
import CreateAdvertiser from "./pages/Settings/SettingsComponents/CreateAdvertiser";
import PreferredDeals from "./pages/Settings/SettingsComponents/PreferredDeals";
import LabelsSettings from "./pages/Settings/SettingsComponents/LabelsSettings";

import WebsiteRoot from "./pages/Settings/SettingsComponents/Website/WebsiteRoot";
import Website from "./pages/Settings/SettingsComponents/Website/Website";
import WebsiteDetails from "./pages/Settings/SettingsComponents/Website/Details";
import AdvertorialUI from "./pages/Settings/SettingsComponents/Website/AdvertorialUI";
import AdvertorialHosting from "./pages/Settings/SettingsComponents/Website/AdvertorialHosting";
import Templates from "./pages/Settings/SettingsComponents/Templates";
import RateCard from "./pages/Settings/SettingsComponents/Website/RateCard";
import NotFound from "./pages/NotFound";
import RevenueNew from "./pages/Settings/SettingsComponents/RevenueNew";
import AccountReporting from "./pages/Settings/SettingsComponents/AccountReporting";
import AgencyCost from "./pages/Settings/SettingsComponents/AgencyCost";
import AccountReportingII from "./pages/Settings/SettingsComponents/AccountReportingII";
import AccountReportingWebsites from "./pages/Settings/SettingsComponents/AccountReportingWebsites";
import ArticleTemplates from "./pages/Library/ArticleTemplates";
import MarketplaceCampaigns from "./pages/MarketplaceCampaigns";

function App() {
  const { isLoading, isAuthenticated, error, loginWithRedirect, user, auth0User, ...currentUserContext } =
    useFetchCurrentUser();
  const rollbar = useRollbar();

  useRollbarPerson({
    id: user?._id,
    fullname: user?.fullname,
    email: user?.email,
  });

  // Identify user in Mixpanel when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      identifyUser(user, auth0User?.sub);
      trackEvent('user_logged_in', {
        companyType: user.company.type,
        userRole: user.role,
      });
    }
  }, [isAuthenticated, user, auth0User]);

  useEffect(() => {
    if (!error) {
      return;
    }

    rollbar.error("Error fetching current user", error);
    trackEvent('error_occurred', {
      errorType: 'user_fetch_error',
      errorMessage: error.message,
    });
  }, [error, rollbar]);

  const isSignup = useMatch("signup");
  const [params] = useSearchParams();
  const email = params.get("email");

  if (isSignup) {
    trackEvent('user_signup_started', { email });
    loginWithRedirect({
      screen_hint: "signup",
      login_hint: email,
    });
    return <Spinner />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return (
      <HomeAnom
        onClickLogin={() => {
          trackEvent('login_clicked');
          loginWithRedirect();
        }}
        onSignupClick={() => {
          trackEvent('signup_clicked');
          loginWithRedirect({ screen_hint: "signup" });
        }}
      />
    );
  }

  if (!user) {
    return <Spinner />
  }

  const campaignPages = (
    <Route element={<Campaign />}>
      <Route path='details' element={<Details />} />
      <Route path='websites' element={<Websites />} />
      <Route path='meta-article' element={<MetaArticle />} />
      <Route path='teasers' element={<TrafficDrivers />} />
      <Route path='reportnewtime' element={<ReportNewTime />} />
      <Route path='report' element={<ReportNewTimePrototype />} />
      <Route path='report-old' element={<Report />} />
      <Route path='videoreport' element={<ReportVideo />} />
      <Route path='reportnew' element={<ReportNew />} />
    </Route>
  )

  const publicationPages = (
    <Route
      path="publications/:publicationId"
      element={<Publication />}
    >
      <Route path="details" element={<PublicationDetails />} />
      <Route path="content" element={<PublicationContent />} />
    </Route>
  )
  return (
    <CurrentUserContext.Provider value={{user, ...currentUserContext}}>
      <Layout>
        <Toaster position="top-right" />
        <Routes>
          <Route element={<Home />}>
            <Route index element={<RecentList />} />
            <Route path="waiting" element={<WaitingList />} />
            <Route path="ready" element={<ReadyList />} />
            <Route path="active" element={<ActiveList />} />
          </Route>
          <Route path="settings" element={<SettingsAdvanced />}>
            <Route path="profile" element={<AdvancedProfile />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="billing" element={<AdvancedBilling />} />
            <Route path="accountreporting" element={<AccountReporting />} />
            <Route path="campaignreporting" element={<AccountReportingII />} />
            <Route path="accountreportingWebsites" element={<AccountReportingWebsites />} />
            <Route path="cost" element={<AgencyCost />} />
            <Route path="account" element={<AdvancedAccount />} />
            <Route path="users" element={<CreateUserForm />} />
            <Route path="websites">
              <Route index element={<AdvancedWebsites />} />
              <Route path=":websiteId" element={<WebsiteRoot />}>
                <Route element={<Website />}>
                  <Route path="details" element={<WebsiteDetails />} />
                  <Route path="advertorial-ui" element={<AdvertorialUI />} />
                  <Route
                    path="advertorial-hosting"
                    element={<AdvertorialHosting />}
                  />
                  <Route path="rate-card" element={<RateCard />} />
                </Route>
              </Route>
            </Route>
            <Route path="templates" element={<Templates />} />
            <Route path="placements" element={<AdvancedPlacements />} />
            <Route path="advertisers" element={<CreateAdvertiser />} />
            <Route path="preferred-deals" element={<PreferredDeals />} />
            <Route path="labels" element={<LabelsSettings />} />
          </Route>
          <Route path="create">
            <Route path="campaign" element={<CreateCampaignRoot />}>
              <Route index element={<CreateCampaign />} />
              <Route
                path="select-websites"
                element={<MarketplaceGuard><SelectWebsites /></MarketplaceGuard>}
              />
              <Route
                path="create-meta-article"
                element={<CreateMetaArticle />}
              />
            </Route>
            <Route path="display-campaign" element={<CreateCampaignRoot isNativeDisplay />}>
              <Route index element={<CreateCampaign isNativeDisplay/>} />
              <Route
                path="select-websites"
                element={<MarketplaceGuard><SelectWebsites isNativeDisplay/></MarketplaceGuard>}
              />
            </Route>
          </Route>
          <Route path="campaigns">
            <Route index element={<CampaignsTest />} />
            <Route path=":campaignId" element={<CampaignRoot />}>
              {campaignPages}
              {publicationPages}
            </Route>
          </Route>
          <Route path="marketplace-campaigns">
            <Route index element={<MarketplaceCampaigns />} />
            <Route path=":campaignId" element={<CampaignRoot marketplace/>}>
              {campaignPages}
              {publicationPages}
            </Route>
          </Route>
          <Route path="library">
            <Route path="article-templates" element={<ArticleTemplates />}/>
          </Route>
          <Route path="email" element={<EmailTemplate />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </CurrentUserContext.Provider>
  );
}

export default App;
